import { useRef, useState, type ReactElement } from 'react';
import { Form, Link, redirect, useActionData, useLoaderData, useNavigation } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Button, FieldError, invalidProps } from '@marketplace/ui';
import {
  JobUrgencySchema,
  type CategorySummary,
  type JobAddressInput,
} from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import {
  AUCTION_DURATIONS,
  readAuctionChoice,
  type AuctionFormField,
} from '../features/auctions/auction-form.js';
import { AskPublishTerms, AskUnavailable, useAskCta } from '../features/checkout/ask/Ask.js';
import {
  charging,
  jobPath,
  loadAskPrices,
  publishCheckoutInput,
  startAskCheckout,
  type AskCharging,
  type AskPricesRead,
} from '../features/checkout/ask/ask.js';
import { parseJobForm, type JobFormField } from '../features/jobs/posting.js';
import { loadCategories } from '../shared/categories.js';
import { queryKeys, routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';

/**
 * Posting a job — `W4-T10` §2.1, §2.2. `W4-T01`'s tail: the API has taken jobs since that ticket, and
 * until this page only the seeder put one in front of a provider.
 *
 * **One submit, two calls.** The draft, then the transition, then the job's own page. A separate
 * *save draft* step would be a second screen with no alpha value. When the create succeeds and the
 * publish does not, the draft exists — so the action still goes to it, and `/jobs/:id` offers
 * *Publicar* on any draft rather than this page pretending nothing was saved.
 *
 * **`W4-T11`**: the job says where it is — four lines, placed by postcode on the server — and may be
 * remembered as the client's saved address, which the next visit offers to fill in.
 */
interface JobNewData {
  locale: string;
  categories: CategorySummary[];
  /** `W4-T11` §3.5 — what *"usar mi dirección guardada"* fills in; `null` hides the box. */
  saved: JobAddressInput | null;
  /** `W5-T12` §4.10 — what publishing costs, said before the client presses the button. */
  askPrices: AskPricesRead;
}

type JobNewActionData =
  { errors: Partial<Record<JobFormField | AuctionFormField, string>> } | { failure: string };

export async function loader({ params, context }: LoaderFunctionArgs): Promise<JobNewData> {
  const locale = localeOf(params);

  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  // The shell loaded these already under the same key, so this is a cache read. It degrades to an
  // empty list (`shared/categories.ts`), and the form then says it cannot offer a trade (§2.2).
  const categories = await loadCategories(context, locale);

  // `W4-T11` §3.5. Read fresh rather than cached — the job just posted may have remembered a new one
  // — and degraded to `null` on any failure: the form still works, it only loses the shortcut.
  const { api } = context.get(routeContext);
  const saved = await api.getSavedAddress().catch(() => null);

  return { locale, categories, saved, askPrices: await loadAskPrices(context) };
}

export async function action({
  params,
  request,
  context,
}: ActionFunctionArgs): Promise<JobNewActionData | Response> {
  const locale = localeOf(params);
  const form = await request.formData();
  const parsed = parseJobForm(form);
  // `W6-T06`: presupuestos or an auction. Both halves report together — one submit, every problem.
  const priced = readAuctionChoice(form);
  if (!parsed.ok || !priced.ok) {
    return {
      errors: { ...(parsed.ok ? {} : parsed.errors), ...(priced.ok ? {} : priced.errors) },
    };
  }

  const { queryClient, api } = context.get(routeContext);

  let id: string;
  try {
    id = (await api.createJob(parsed.input)).id;
  } catch {
    // Nothing was saved, so the form stays with what was typed — the inputs are uncontrolled and
    // this page does not remount.
    return { failure: 'jobForm.error.failed' };
  }

  // `W5-T12` §4.3: with charging on, publishing is paying — the draft is saved, then Checkout. Any
  // answer but Checkout's page lands on the draft, whose page says what it can (`?ask=paid` for a
  // payment already being confirmed). With payments unavailable, the draft is saved and not published.
  const prices = await loadAskPrices(context);
  let next: Response = redirect(jobPath(locale, id));
  if (charging(prices)) {
    const paid = await startAskCheckout(
      context,
      locale,
      id,
      () => api.startPublishCheckout(id, publishCheckoutInput(priced.choice)),
      'job.publish.error',
    );
    if (paid instanceof Response) next = paid;
  } else if (prices !== 'unavailable') {
    try {
      if (priced.choice.mode === 'AUCTION') await api.publishAuction(id, priced.choice.input);
      else await api.publishJob(id);
    } catch {
      // The draft exists. Its page says it is a draft and offers to publish it (§2.1, AC7) — as
      // presupuestos: a failed auction publish lands on the same page (`W6-T06` spec §5).
    }
  }

  await queryClient.invalidateQueries({ queryKey: queryKeys.myJobs(), refetchType: 'all' });
  return next;
}

/** The four address inputs, in the order `ProviderForm` asks them, with the same autofill hints. */
const ADDRESS_FIELDS = [
  { name: 'line1', autoComplete: 'address-line1', maxLength: 200 },
  { name: 'postalCode', autoComplete: 'postal-code', maxLength: 5, inputMode: 'numeric' },
  { name: 'city', autoComplete: 'address-level2', maxLength: 100 },
  { name: 'province', autoComplete: 'address-level1', maxLength: 100 },
] as const;

export function Component(): ReactElement {
  const { t } = useTranslation();
  const { locale, categories, saved, askPrices } = useLoaderData<JobNewData>();
  const addressForm = useRef<HTMLFieldSetElement>(null);

  /**
   * *"Same as my saved address"* **fills** the lines and leaves them editable — the operator's
   * *"as free as possible"* (`MEM-2026-09-28-identity-3`). Unticking leaves what is there: clearing a
   * street somebody may have corrected by hand would be the form undoing their work.
   */
  function fillFromSaved(checked: boolean): void {
    if (!checked || saved === null || addressForm.current === null) return;
    for (const { name } of ADDRESS_FIELDS) {
      const input = addressForm.current.querySelector<HTMLInputElement>(`[name="${name}"]`);
      if (input !== null) input.value = saved[name];
    }
  }
  const result = useActionData<JobNewActionData>();
  const navigation = useNavigation();
  // Presentation only: which extra fields to show. The form posts the radio either way.
  const [priceMode, setPriceMode] = useState<'QUOTES' | 'AUCTION'>('QUOTES');

  const errors = result !== undefined && 'errors' in result ? result.errors : {};
  const failure = result !== undefined && 'failure' in result ? result.failure : null;

  return (
    <article data-testid="job-new">
      <p>
        <Link to={`/${locale}/jobs`}>{t('job.back')}</Link>
      </p>
      <h1>{t('jobForm.title')}</h1>
      <p className="mp-lead">{t('jobForm.intro')}</p>

      <Form method="post" className="mp-form mp-stack-form" data-testid="job-form" noValidate>
        <label htmlFor="job-title">{t('jobForm.field.title')}</label>
        <input
          id="job-title"
          name="title"
          maxLength={140}
          autoComplete="off"
          {...invalidProps(errors.title, 'job-title-error')}
        />
        <FieldError
          id="job-title-error"
          message={errors.title === undefined ? undefined : t(errors.title)}
        />

        <label htmlFor="job-description">{t('jobForm.field.description')}</label>
        <textarea
          id="job-description"
          name="description"
          rows={5}
          maxLength={5000}
          {...invalidProps(errors.description, 'job-description-error')}
        />
        <FieldError
          id="job-description-error"
          message={errors.description === undefined ? undefined : t(errors.description)}
        />

        <fieldset {...invalidProps(errors.categorySlugs, 'job-categories-error')}>
          <legend>{t('jobForm.field.categories')}</legend>
          {categories.length === 0 ? (
            <p className="mp-meta">{t('jobForm.categories.unavailable')}</p>
          ) : (
            categories.map((category) => (
              <label key={category.slug} className="mp-check">
                <input type="checkbox" name="categorySlugs" value={category.slug} /> {category.name}
              </label>
            ))
          )}
        </fieldset>
        <FieldError
          id="job-categories-error"
          message={errors.categorySlugs === undefined ? undefined : t(errors.categorySlugs)}
        />

        <label htmlFor="job-urgency">{t('jobForm.field.urgency')}</label>
        <select id="job-urgency" name="urgency" defaultValue="">
          <option value="">{t('jobForm.urgency.none')}</option>
          {JobUrgencySchema.options.map((urgency) => (
            <option key={urgency} value={urgency}>
              {t(`search.when.${urgency}`)}
            </option>
          ))}
        </select>

        <fieldset {...invalidProps(errors.budget, 'job-budget-error')}>
          <legend>{t('jobForm.field.budget')}</legend>
          <p className="mp-meta">{t('jobForm.budget.hint')}</p>
          <label htmlFor="job-budget-min">{t('jobForm.field.budgetMin')}</label>
          <input id="job-budget-min" name="budgetMin" inputMode="decimal" autoComplete="off" />
          <label htmlFor="job-budget-max">{t('jobForm.field.budgetMax')}</label>
          <input id="job-budget-max" name="budgetMax" inputMode="decimal" autoComplete="off" />
        </fieldset>
        <FieldError
          id="job-budget-error"
          message={errors.budget === undefined ? undefined : t(errors.budget)}
        />

        <fieldset ref={addressForm}>
          <legend>{t('jobForm.field.address')}</legend>
          <p className="mp-meta">{t('jobForm.address.hint')}</p>
          {saved === null ? null : (
            <label className="mp-check">
              <input
                type="checkbox"
                name="useSavedAddress"
                onChange={(event) => {
                  fillFromSaved(event.currentTarget.checked);
                }}
              />{' '}
              {t('jobForm.address.useSaved')}
            </label>
          )}
          {ADDRESS_FIELDS.map(({ name, ...attributes }) => (
            <div key={name}>
              <label htmlFor={`job-${name}`}>{t(`jobForm.field.${name}`)}</label>
              <input
                id={`job-${name}`}
                name={name}
                {...attributes}
                {...invalidProps(errors[name], `job-${name}-error`)}
              />
              <FieldError
                id={`job-${name}-error`}
                message={errors[name] === undefined ? undefined : t(errors[name])}
              />
            </div>
          ))}
          <label className="mp-check">
            <input type="checkbox" name="rememberAddress" /> {t('jobForm.address.remember')}
          </label>
        </fieldset>

        <fieldset data-testid="price-mode">
          <legend>{t('auctionForm.mode.legend')}</legend>
          <label className="mp-check">
            <input
              type="radio"
              name="priceMode"
              value="QUOTES"
              checked={priceMode === 'QUOTES'}
              onChange={() => setPriceMode('QUOTES')}
            />{' '}
            {t('auctionForm.mode.QUOTES')}
          </label>
          <p className="mp-meta">{t('auctionForm.mode.QUOTES.hint')}</p>
          <label className="mp-check">
            <input
              type="radio"
              name="priceMode"
              value="AUCTION"
              checked={priceMode === 'AUCTION'}
              onChange={() => setPriceMode('AUCTION')}
            />{' '}
            {t('auctionForm.mode.AUCTION')}
          </label>
          <p className="mp-meta">{t('auctionForm.mode.AUCTION.hint')}</p>

          {priceMode === 'AUCTION' ? (
            <div data-testid="auction-fields">
              <label htmlFor="auction-duration">{t('auctionForm.duration')}</label>
              <select
                id="auction-duration"
                name="durationDays"
                defaultValue="3"
                {...invalidProps(errors.duration, 'auction-duration-error')}
              >
                {AUCTION_DURATIONS.map((days) => (
                  <option key={days} value={days}>
                    {t('auctionForm.days', { count: days })}
                  </option>
                ))}
              </select>
              <FieldError
                id="auction-duration-error"
                message={errors.duration === undefined ? undefined : t(errors.duration)}
              />

              <label htmlFor="auction-max-price">{t('auctionForm.maxPrice')}</label>
              <input
                id="auction-max-price"
                name="maxPrice"
                inputMode="decimal"
                autoComplete="off"
                {...invalidProps(errors.maxPrice, 'auction-max-price-error')}
              />
              <p className="mp-meta">{t('auctionForm.maxPrice.hint')}</p>
              <FieldError
                id="auction-max-price-error"
                message={errors.maxPrice === undefined ? undefined : t(errors.maxPrice)}
              />
            </div>
          ) : null}
        </fieldset>

        {/* `W5-T12` §4.10: the price and what it buys, or that payments cannot be taken now. */}
        {charging(askPrices) ? (
          <AskPublishTerms prices={askPrices} mode={priceMode} locale={locale} />
        ) : askPrices === 'unavailable' ? (
          <AskUnavailable />
        ) : null}

        {failure === null ? null : <Alert tone="error">{t(failure)}</Alert>}

        <Button
          type="submit"
          variant="primary"
          isPending={navigation.state === 'submitting'}
          pendingLabel={t('jobForm.pending')}
        >
          {charging(askPrices) ? (
            <AskCtaLabel prices={askPrices} mode={priceMode} locale={locale} />
          ) : (
            t('jobForm.submit')
          )}
        </Button>
      </Form>
    </article>
  );
}

/** `W5-T12`: the submit says what it costs (*Publicar · 2,00 €*), for the mode chosen. */
function AskCtaLabel({
  prices,
  mode,
  locale,
}: {
  prices: AskCharging;
  mode: 'QUOTES' | 'AUCTION';
  locale: string;
}): string {
  return useAskCta(prices, mode, locale);
}
